import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {Activity, Edit2, Palette, Plus, Search, Shapes, Trash2} from 'lucide-react';
import {usePermissions} from '@/hooks/usePermissions';
import {
    api,
    getApiErrorMessage,
    objectColorEndpoints,
    objectIconEndpoints,
    resourceStatusEndpoints,
} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {
    code: '',
    name: '',
    description: '',
    object_color_id: '',
    object_icon_id: '',
    status: '1',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const idFrom = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeOption = (source = {}) => {
    const id = idFrom(source);
    const label = source.label || source.name || source.title || String(id);
    return {
        ...source,
        id,
        name: label,
        label,
        color: source.color || source.hex || source.hex_color || source.value || '',
        icon: source.icon || source.icon_name || source.name || '',
    };
};

const ColorOption = ({color}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        <span
            className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0"
            style={{backgroundColor: color?.color || '#64748b'}}
        />
        <span className="truncate">{color?.label || color?.name || 'Sem nome'}</span>
    </span>
);

const IconOption = ({icon}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        {icon?.icon ? (
            <img src={icon.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0"/>
        ) : (
            <Shapes size={14} className="text-slate-500 flex-shrink-0"/>
        )}
        <span className="truncate">{icon?.label || icon?.name || 'Sem nome'}</span>
    </span>
);

const normalizeResourceStatus = (source = {}, fallback = {}) => {
    const color = source.object_color ?? source.color ?? {};
    const icon = source.object_icon ?? source.icon ?? {};

    return {
        ...fallback,
        ...source,
        id: source.id ?? source.resource_status_id ?? fallback.id,
        uuid: source.uuid ?? fallback.uuid,
        company_id: source.company_id ?? fallback.company_id ?? null,
        code: source.code ?? fallback.code ?? '',
        name: source.name ?? fallback.name ?? '',
        description: source.description ?? fallback.description ?? '',
        object_color_id: source.object_color_id ?? color?.id ?? source.objectColorId ?? fallback.object_color_id ?? null,
        object_icon_id: source.object_icon_id ?? icon?.id ?? source.objectIconId ?? fallback.object_icon_id ?? null,
        status: Number(source.status ?? fallback.status ?? 0),
    };
};

const normalizeResourceStatuses = (payload) => {
    const statuses = collectionPayload(payload, 'resource_statuses');
    const singularStatuses = statuses.length ? statuses : collectionPayload(payload, 'resource_status');
    const namedStatuses = singularStatuses.length ? singularStatuses : collectionPayload(payload, 'statuses');
    const items = namedStatuses.length ? namedStatuses : collectionPayload(payload, 'resourceStatuses');
    return items.map((item, index) => normalizeResourceStatus(item, {id: index + 1}));
};

const resourceStatusPayload = (form) => ({
    code: form.code.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    object_color_id: form.object_color_id ? Number(form.object_color_id) : null,
    object_icon_id: form.object_icon_id ? Number(form.object_icon_id) : null,
    status: Number(form.status),
});

const SETTINGS_ACTION_PERMISSIONS = {
    create: PERMISSIONS.SETTINGS_CREATE,
    read: PERMISSIONS.SETTINGS_READ,
    update: PERMISSIONS.SETTINGS_UPDATE,
    delete: PERMISSIONS.SETTINGS_DELETE,
};

export const ResourceStatusesPage = () => {
    const {can} = usePermissions();
    const [resourceStatuses, setResourceStatuses] = useState([]);
    const [colors, setColors] = useState([]);
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingResourceStatus, setEditingResourceStatus] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadSettings = can(SETTINGS_ACTION_PERMISSIONS.read);
    const canCreateSettings = can(SETTINGS_ACTION_PERMISSIONS.create);
    const canUpdateSettings = can(SETTINGS_ACTION_PERMISSIONS.update);
    const canDeleteSettings = can(SETTINGS_ACTION_PERMISSIONS.delete);
    const showActions = canUpdateSettings || canDeleteSettings;

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            if (!canReadSettings) {
                setResourceStatuses([]);
                setColors([]);
                setIcons([]);
                setError('Nao tem permissao para consultar os estados dos recursos.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const [statusesResponse, colorsResponse, iconsResponse] = await Promise.all([
                    api.get(resourceStatusEndpoints.list),
                    api.get(objectColorEndpoints.list).catch(() => ({data: []})),
                    api.get(objectIconEndpoints.list).catch(() => ({data: []})),
                ]);

                if (!cancelled) {
                    setResourceStatuses(normalizeResourceStatuses(statusesResponse.data));
                    setColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeOption).filter(option => option.id));
                    setIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeOption).filter(option => option.id));
                }
            } catch (requestError) {
                if (!cancelled) {
                    setResourceStatuses([]);
                    setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar os estados dos recursos.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, [canReadSettings]);

    const colorById = useMemo(() => new Map(colors.map(color => [String(color.id), color])), [colors]);
    const iconById = useMemo(() => new Map(icons.map(icon => [String(icon.id), icon])), [icons]);

    const filteredResourceStatuses = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return resourceStatuses;
        return resourceStatuses.filter(resourceStatus =>
            [resourceStatus.code, resourceStatus.name, resourceStatus.description, resourceStatus.uuid]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(q))
        );
    }, [resourceStatuses, search]);

    const openCreate = () => {
        if (!canCreateSettings) {
            toast.error('Nao tem permissao para criar estados de recursos.');
            return;
        }

        setEditingResourceStatus(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (resourceStatus) => {
        if (!canUpdateSettings) {
            toast.error('Nao tem permissao para editar estados de recursos.');
            return;
        }

        setEditingResourceStatus(resourceStatus);
        setForm({
            code: resourceStatus.code || '',
            name: resourceStatus.name || '',
            description: resourceStatus.description || '',
            object_color_id: resourceStatus.object_color_id ? String(resourceStatus.object_color_id) : '',
            object_icon_id: resourceStatus.object_icon_id ? String(resourceStatus.object_icon_id) : '',
            status: String(resourceStatus.status ?? 0),
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (editingResourceStatus && !canUpdateSettings) {
            toast.error('Nao tem permissao para editar estados de recursos.');
            return;
        }

        if (!editingResourceStatus && !canCreateSettings) {
            toast.error('Nao tem permissao para criar estados de recursos.');
            return;
        }

        const code = form.code.trim();
        if (!code) {
            toast.error('Codigo e obrigatorio.');
            return;
        }

        if (!/^[a-z]+$/.test(code)) {
            toast.error('Codigo deve conter apenas letras minusculas.');
            return;
        }

        if (!form.name.trim()) {
            toast.error('Nome e obrigatorio.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingResourceStatus) {
                const response = await api.post(resourceStatusEndpoints.update, {
                    id: editingResourceStatus.id,
                    uuid: editingResourceStatus.uuid,
                    ...resourceStatusPayload(form),
                });
                const updated = normalizeResourceStatus(response.data?.data ?? response.data?.resource_status ?? response.data, {
                    ...editingResourceStatus,
                    ...resourceStatusPayload(form),
                });
                setResourceStatuses(prev => prev.map(resourceStatus => resourceStatus.id === editingResourceStatus.id ? updated : resourceStatus));
                toast.success(`Estado "${updated.name}" atualizado.`);
            } else {
                const response = await api.post(resourceStatusEndpoints.create, resourceStatusPayload(form));
                const created = normalizeResourceStatus(response.data?.data ?? response.data?.resource_status ?? response.data, {
                    id: Date.now(),
                    ...resourceStatusPayload(form),
                });
                setResourceStatuses(prev => [created, ...prev]);
                toast.success(`Estado "${created.name}" criado.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel guardar o estado do recurso.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (resourceStatus) => {
        if (!canDeleteSettings) {
            toast.error('Nao tem permissao para eliminar estados de recursos.');
            return;
        }

        if (!window.confirm(`Eliminar estado "${resourceStatus.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(resourceStatusEndpoints.delete, {
                id: resourceStatus.id,
                uuid: resourceStatus.uuid,
            });
            setResourceStatuses(prev => prev.filter(item => item.id !== resourceStatus.id));
            toast.success(`Estado "${resourceStatus.name}" removido.`);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel remover o estado do recurso.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Estados de Recursos</h1>
                        <p className="text-slate-600 font-primary mt-1">Gerir estados usados para classificar recursos da frota.</p>
                    </div>
                    {canCreateSettings && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-resource-status">
                            <Plus size={18} className="mr-2"/>
                            Novo Estado
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Total</p>
                            <Activity size={18} className="text-blue-600"/>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{resourceStatuses.length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Ativos</p>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">status 1</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{resourceStatuses.filter(resourceStatus => Number(resourceStatus.status) === 1).length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Inativos</p>
                            <Badge className="bg-slate-100 text-slate-700 border-0">status 0</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{resourceStatuses.filter(resourceStatus => Number(resourceStatus.status) !== 1).length}</p>
                    </Card>
                </div>

                <div className="relative max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input
                        className="pl-9"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Pesquisar por codigo, nome, descricao ..."
                        data-testid="resource-status-search"
                    />
                </div>

                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Codigo</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Cor</TableHead>
                                <TableHead>Icone</TableHead>
                                <TableHead>Ativo</TableHead>
                                {showActions && <TableHead className="text-right">Acoes</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filteredResourceStatuses.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={showActions ? 6 : 5} className="text-center text-sm text-slate-400 italic py-10">
                                        {loading ? 'A carregar estados...' : error || 'Sem estados para mostrar.'}
                                    </TableCell>
                                </TableRow>
                            ) : filteredResourceStatuses.map(resourceStatus => {
                                const color = colorById.get(String(resourceStatus.object_color_id));
                                const icon = iconById.get(String(resourceStatus.object_icon_id));
                                return (
                                    <TableRow key={resourceStatus.id} data-testid={`resource-status-row-${resourceStatus.id}`}>
                                        <TableCell>
                                            <code className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded">
                                                {resourceStatus.code || '-'}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            <p className="font-medium text-slate-900">{resourceStatus.name}</p>
                                            {resourceStatus.description && <p className="text-xs text-slate-500 line-clamp-1">{resourceStatus.description}</p>}
                                        </TableCell>
                                        <TableCell>
                                            {color ? (
                                                <span className="inline-flex items-center gap-2 text-sm text-slate-700">
                                                    <ColorOption color={color}/>
                                                </span>
                                            ) : <span className="text-xs text-slate-400 italic">Sem cor</span>}
                                        </TableCell>
                                        <TableCell>
                                            {icon ? (
                                                <span className="text-sm text-slate-700"><IconOption icon={icon}/></span>
                                            ) : <span className="text-xs text-slate-400 italic">Sem icone</span>}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={Number(resourceStatus.status) === 1 ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-100 text-slate-700 border-0'}>
                                                {Number(resourceStatus.status) === 1 ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </TableCell>
                                        {showActions && (
                                            <TableCell className="text-right">
                                                {canUpdateSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(resourceStatus)} disabled={submitting} data-testid={`edit-resource-status-${resourceStatus.id}`}>
                                                        <Edit2 size={14} className="text-slate-600"/>
                                                    </Button>
                                                )}
                                                {canDeleteSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(resourceStatus)} disabled={submitting} data-testid={`delete-resource-status-${resourceStatus.id}`}>
                                                        <Trash2 size={14} className="text-red-500"/>
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-xl" data-testid="resource-status-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingResourceStatus ? 'Editar Estado de Recurso' : 'Novo Estado de Recurso'}</DialogTitle>
                            <DialogDescription>Configurar codigo, nome, descricao, cor, icone e estado do recurso.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Codigo *</Label>
                                <Input
                                    value={form.code}
                                    onChange={event => setForm(prev => ({...prev, code: event.target.value.toLowerCase()}))}
                                    pattern="[a-z]+"
                                    data-testid="resource-status-code"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} data-testid="resource-status-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Descricao</Label>
                                <Textarea rows={3} value={form.description} onChange={event => setForm(prev => ({...prev, description: event.target.value}))} data-testid="resource-status-description"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Palette size={13}/> Cor</Label>
                                    <Select value={form.object_color_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_color_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="resource-status-color">
                                            <SelectValue>
                                                {form.object_color_id
                                                    ? <ColorOption color={colorById.get(String(form.object_color_id))}/>
                                                    : 'Sem cor'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem cor</SelectItem>
                                            {colors.map(color => (
                                                <SelectItem key={color.id} value={String(color.id)}>
                                                    <ColorOption color={color}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Shapes size={13}/> Icone</Label>
                                    <Select value={form.object_icon_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_icon_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="resource-status-icon">
                                            <SelectValue>
                                                {form.object_icon_id
                                                    ? <IconOption icon={iconById.get(String(form.object_icon_id))}/>
                                                    : 'Sem icone'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem icone</SelectItem>
                                            {icons.map(icon => (
                                                <SelectItem key={icon.id} value={String(icon.id)}>
                                                    <IconOption icon={icon}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Ativo</Label>
                                <Select value={form.status} onValueChange={value => setForm(prev => ({...prev, status: value}))}>
                                    <SelectTrigger data-testid="resource-status-active"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Ativo</SelectItem>
                                        <SelectItem value="0">Inativo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={submitting || (editingResourceStatus ? !canUpdateSettings : !canCreateSettings)} className="bg-blue-600 hover:bg-blue-700" data-testid="resource-status-submit">
                                {submitting ? 'A guardar...' : editingResourceStatus ? 'Guardar' : 'Criar Estado'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
